import { useCallback, useEffect, useMemo, useState, type ComponentType, type RefObject } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import {
  defaultOsintExpandedIds,
  type FootprintOsintKind,
  type FootprintOsintLeafRole,
  type FootprintOsintTreeNode,
} from '../../lib/employerFootprintCopy';

/* ── Spatial rhythm (8px grid) ── */
const ROW_H = 84;
const BRANCH_GAP = 36;
const CIRCLE_R = 6;
const BRANCH_HIT = 44;
const ROOT_CARD_W = 268;
const BRANCH_CARD_W = 252;
const LEAF_LINE_H = 18;
const LEAF_CARD_PAD = 32;
const LEAF_SLOT_GAP = 14;
const CANVAS_PAD_BOTTOM = 48;
const CANVAS_PAD_LEFT = 20;
const COL_STEP = 296;
const ANIM_MS = 220;
const ANIM_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const RADIUS_L = 16;
const RADIUS_M = 12;
const RADIUS_S = 8;

type KindStyle = {
  color: string;
  fill: string;
  fillHi: string;
  glow: string;
  Icon: ComponentType<SvgIconProps>;
  desc: string;
};

/** Category hues — intelligence grouping, not verdicts. */
const KIND_STYLE: Record<Exclude<FootprintOsintKind, 'leaf'>, KindStyle> = {
  company: {
    color: '#1e3a5f',
    fill: '#dce6f4',
    fillHi: '#f4f7fb',
    glow: 'rgba(30,58,95,0.12)',
    Icon: BusinessRoundedIcon,
    desc: 'Employer under investigation',
  },
  ownership: {
    color: '#5c4a2a',
    fill: '#ebe4d6',
    fillHi: '#f8f5ef',
    glow: 'rgba(92,74,42,0.1)',
    Icon: AccountBalanceRoundedIcon,
    desc: 'Parent structure & ownership',
  },
  location: {
    color: '#1a6368',
    fill: '#cce8e6',
    fillHi: '#ecf7f5',
    glow: 'rgba(26,99,104,0.12)',
    Icon: PlaceRoundedIcon,
    desc: 'Addresses & geographic signals',
  },
  digital: {
    color: '#4a3d7a',
    fill: '#ddd6ef',
    fillHi: '#f3f0fa',
    glow: 'rgba(74,61,122,0.1)',
    Icon: LanguageRoundedIcon,
    desc: 'Website, phone & public contact',
  },
  sector: {
    color: '#3d5166',
    fill: '#d4dce6',
    fillHi: '#eef2f6',
    glow: 'rgba(61,81,102,0.1)',
    Icon: CategoryRoundedIcon,
    desc: 'Industry & classification',
  },
  entity: {
    color: '#45505c',
    fill: '#d8dce1',
    fillHi: '#f0f2f4',
    glow: 'rgba(69,80,92,0.08)',
    Icon: ArticleRoundedIcon,
    desc: 'Registry & incorporation record',
  },
  people: {
    color: '#1f5c52',
    fill: '#cce5df',
    fillHi: '#ecf6f3',
    glow: 'rgba(31,92,82,0.1)',
    Icon: PeopleRoundedIcon,
    desc: 'Leadership & founders',
  },
};

const LEGEND_KINDS: Array<Exclude<FootprintOsintKind, 'leaf' | 'company'>> = [
  'location',
  'digital',
  'ownership',
  'people',
  'sector',
  'entity',
];

function solidCardFace(style: KindStyle, emphasis?: 'open' | 'search') {
  return {
    background: `linear-gradient(165deg, ${style.fillHi} 0%, ${style.fill} 100%)`,
    border: `1px solid ${alpha(style.color, emphasis ? 0.42 : 0.28)}`,
    boxShadow:
      emphasis === 'open'
        ? `0 0 0 1px ${alpha(style.color, 0.15)}, 0 8px 24px ${style.glow}, inset 0 1px 0 rgba(255,255,255,0.85)`
        : `inset 0 1px 0 rgba(255,255,255,0.78), 0 2px 8px ${style.glow}`,
  };
}

type LaidOutNode = {
  id: string;
  node: FootprintOsintTreeNode;
  depth: number;
  x: number;
  y: number;
  color: string;
  kind: Exclude<FootprintOsintKind, 'leaf'>;
  hasKids: boolean;
  open: boolean;
  isLeaf: boolean;
  kidCount: number;
  searchHit: boolean;
};

type Edge = { from: LaidOutNode; to: LaidOutNode };

type LayoutMetrics = {
  originX: number;
  colStep: number;
  leafMaxW: number;
};

function nodeMatches(node: FootprintOsintTreeNode, query: string): boolean {
  if (!query) return true;
  const hay = `${node.label} ${node.value ?? ''}`.toLowerCase();
  if (hay.includes(query)) return true;
  return (node.children ?? []).some((child) => nodeMatches(child, query));
}

function nodeSearchHit(node: FootprintOsintTreeNode, query: string): boolean {
  if (!query) return false;
  return `${node.label} ${node.value ?? ''}`.toLowerCase().includes(query);
}

function visibleChildren(node: FootprintOsintTreeNode, query: string): FootprintOsintTreeNode[] {
  return (node.children ?? []).filter((child) => nodeMatches(child, query));
}

function resolveKind(
  node: FootprintOsintTreeNode,
  inherited: Exclude<FootprintOsintKind, 'leaf'>,
): Exclude<FootprintOsintKind, 'leaf'> {
  if (node.kind && node.kind !== 'leaf') return node.kind;
  return inherited;
}

function isBranchOpen(node: FootprintOsintTreeNode, query: string, expanded: Set<string>): boolean {
  return expanded.has(node.id) && visibleChildren(node, query).length > 0;
}

function branchGapAfter(
  depth: number,
  child: FootprintOsintTreeNode,
  next: FootprintOsintTreeNode | undefined,
  query: string,
  expanded: Set<string>,
): number {
  if (depth !== 0 || !next) return 0;
  if (isBranchOpen(child, query, expanded) || isBranchOpen(next, query, expanded)) return BRANCH_GAP;
  return 0;
}

function leafSlotHeight(node: FootprintOsintTreeNode, leafMaxW: number): number {
  const valueLen = (node.value ?? '').trim().length;
  const charsPerLine = Math.max(24, Math.floor(leafMaxW / 9));
  const valueLines = valueLen === 0 ? 0 : Math.ceil(valueLen / charsPerLine);
  const provenanceExtra = node.leafRole ? 22 : 0;
  return LEAF_CARD_PAD + provenanceExtra + LEAF_LINE_H + valueLines * LEAF_LINE_H + LEAF_SLOT_GAP;
}

function closedSlotHeight(node: FootprintOsintTreeNode, leafMaxW: number): number {
  return node.children?.length ? ROW_H : leafSlotHeight(node, leafMaxW);
}

function subtreeHeight(
  node: FootprintOsintTreeNode,
  query: string,
  expanded: Set<string>,
  depth: number,
  metrics: LayoutMetrics,
): number {
  const kids = visibleChildren(node, query);
  if (kids.length === 0 || !expanded.has(node.id)) return closedSlotHeight(node, metrics.leafMaxW);
  let h = 0;
  kids.forEach((child, i) => {
    h += subtreeHeight(child, query, expanded, depth + 1, metrics);
    h += branchGapAfter(depth, child, kids[i + 1], query, expanded);
  });
  return Math.max(h, ROW_H);
}

function layoutTree(
  node: FootprintOsintTreeNode,
  query: string,
  expanded: Set<string>,
  metrics: LayoutMetrics,
): { nodes: LaidOutNode[]; edges: Edge[]; width: number; height: number } {
  const nodes: LaidOutNode[] = [];
  const edges: Edge[] = [];
  const originY = 48;

  function walk(
    current: FootprintOsintTreeNode,
    depth: number,
    y0: number,
    inherited: Exclude<FootprintOsintKind, 'leaf'>,
    parent: LaidOutNode | null,
  ): LaidOutNode {
    const kids = visibleChildren(current, query);
    const kind = resolveKind(current, inherited);
    const open = isBranchOpen(current, query, expanded);
    const blockH = subtreeHeight(current, query, expanded, depth, metrics);
    const laid: LaidOutNode = {
      id: current.id,
      node: current,
      depth,
      x: metrics.originX + depth * metrics.colStep,
      y: y0 + blockH / 2,
      color: KIND_STYLE[kind].color,
      kind,
      hasKids: kids.length > 0,
      open,
      isLeaf: !current.children?.length,
      kidCount: kids.length,
      searchHit: nodeSearchHit(current, query),
    };
    nodes.push(laid);
    if (parent) edges.push({ from: parent, to: laid });
    if (open) {
      let cursor = y0;
      kids.forEach((child, i) => {
        walk(child, depth + 1, cursor, kind, laid);
        cursor += subtreeHeight(child, query, expanded, depth + 1, metrics);
        cursor += branchGapAfter(depth, child, kids[i + 1], query, expanded);
      });
    }
    return laid;
  }

  walk(node, 0, originY, 'company', null);
  const maxX = nodes.reduce((m, n) => Math.max(m, n.x), metrics.originX);
  const bottom = nodes.reduce((m, n) => {
    const half = (n.isLeaf ? leafSlotHeight(n.node, metrics.leafMaxW) : ROW_H) / 2;
    return Math.max(m, n.y + half);
  }, originY);
  return {
    nodes,
    edges,
    width: maxX + BRANCH_CARD_W + metrics.leafMaxW + 48,
    height: Math.max(bottom + CANVAS_PAD_BOTTOM, 300),
  };
}

function collectBranchIds(node: FootprintOsintTreeNode): string[] {
  const ids: string[] = [];
  function walk(current: FootprintOsintTreeNode) {
    if (current.children?.length) {
      ids.push(current.id);
      current.children.forEach(walk);
    }
  }
  walk(node);
  return ids;
}

function collectOpenAncestors(node: FootprintOsintTreeNode, query: string): string[] {
  const ids: string[] = [];
  function walk(current: FootprintOsintTreeNode, ancestors: string[]): boolean {
    const self = `${current.label} ${current.value ?? ''}`.toLowerCase().includes(query);
    let hit = self;
    for (const child of current.children ?? []) {
      if (walk(child, [...ancestors, current.id])) hit = true;
    }
    if (hit) ids.push(...ancestors, current.id);
    return hit;
  }
  walk(node, []);
  return ids;
}

function countSearchHits(node: FootprintOsintTreeNode, query: string): number {
  if (!query) return 0;
  let n = nodeSearchHit(node, query) ? 1 : 0;
  for (const child of node.children ?? []) n += countSearchHits(child, query);
  return n;
}

function normalizeHttpUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (/^[\w.-]+\.[a-z]{2,}/i.test(t)) return `https://${t}`;
  return null;
}

function displayLink(value: string): string {
  return value.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function bezier(from: LaidOutNode, to: LaidOutNode): string {
  const mid = (to.x - from.x) * 0.52;
  return `M ${from.x} ${from.y} C ${from.x + mid} ${from.y}, ${to.x - mid} ${to.y}, ${to.x} ${to.y}`;
}

function leafProvenance(role?: FootprintOsintLeafRole): { label: string; hint: string } | null {
  if (role === 'declared-address') return { label: 'DECLARED', hint: 'Application employment record' };
  if (role === 'ai-footprint') return { label: 'OSINT SIGNAL', hint: 'AI footprint inference' };
  if (role === 'address-distance') return { label: 'GEOSPATIAL', hint: 'Distance between signals' };
  return null;
}

const valueFullSx = {
  display: 'block',
  overflow: 'visible',
  whiteSpace: 'normal' as const,
  overflowWrap: 'anywhere' as const,
  wordBreak: 'break-word' as const,
  fontSize: '0.8125rem',
  lineHeight: 1.4,
};

function ConnectorDot({ color, open, hasKids }: { color: string; open: boolean; hasKids: boolean }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: CIRCLE_R * 2,
        height: CIRCLE_R * 2,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        bgcolor: open && hasKids ? color : 'background.paper',
        flexShrink: 0,
        boxShadow: hasKids ? `0 0 0 3px ${alpha(color, 0.14)}` : 'none',
        transition: 'background-color 180ms ease, box-shadow 180ms ease',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      {hasKids ? (
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 12,
            color: open ? '#fff' : color,
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 180ms ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      ) : null}
    </Box>
  );
}

function RootEmployerCard({ laid }: { laid: LaidOutNode }) {
  const style = KIND_STYLE.company;
  const { Icon } = style;
  const factorLabel =
    laid.kidCount === 1 ? '1 factor group' : `${laid.kidCount} factor groups`;

  return (
    <Box
      sx={{
        width: ROOT_CARD_W,
        borderRadius: `${RADIUS_L}px`,
        ...solidCardFace(style, laid.open ? 'open' : undefined),
        background: (t) =>
          t.palette.mode === 'dark'
            ? `linear-gradient(165deg, ${alpha('#1e293b', 0.95)} 0%, ${alpha('#0f172a', 0.98)} 100%)`
            : `linear-gradient(165deg, #f8fafc 0%, #eef2f7 55%, ${style.fillHi} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse 90% 80% at 0% 0%, ${style.glow}, transparent 70%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', px: 1.75, py: 1.35 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: `${RADIUS_S}px`,
              bgcolor: alpha(style.color, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 20, color: style.color }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: alpha(style.color, 0.75), fontWeight: 700, letterSpacing: 0.6, lineHeight: 1.2 }}
            >
              Employer identity
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: style.color,
                lineHeight: 1.25,
                mt: 0.15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
              title={laid.node.label}
            >
              {laid.node.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.35, display: 'block' }}>
              {factorLabel} · Advisory OSINT
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

function CategoryCard({
  laid,
  onToggle,
}: {
  laid: LaidOutNode;
  onToggle: (id: string) => void;
}) {
  const style = KIND_STYLE[laid.kind];
  const { Icon } = style;
  const signal = laid.node.signal;
  const factorLabel = laid.kidCount === 1 ? '1 factor' : `${laid.kidCount} factors`;
  const emphasis = laid.open ? 'open' : laid.searchHit ? 'search' : undefined;
  const controlsId = `osint-branch-panel-${laid.id}`;

  return (
    <Box
      component="button"
      type="button"
      aria-expanded={laid.hasKids ? laid.open : undefined}
      aria-controls={laid.hasKids ? controlsId : undefined}
      aria-label={
        laid.hasKids
          ? `${laid.open ? 'Collapse' : 'Expand'} ${laid.node.label}, ${factorLabel}${
              signal?.type === 'address-differs' ? ', address differs' : ''
            }`
          : laid.node.label
      }
      disabled={!laid.hasKids}
      onClick={() => {
        if (laid.hasKids) onToggle(laid.id);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: BRANCH_HIT,
        width: BRANCH_CARD_W + CIRCLE_R * 2 + 12,
        m: 0,
        p: 0,
        border: 'none',
        bgcolor: 'transparent',
        cursor: laid.hasKids ? 'pointer' : 'default',
        textAlign: 'left',
        borderRadius: `${RADIUS_M}px`,
        transition: 'transform 180ms ease, opacity 180ms ease',
        opacity: laid.open || !laid.hasKids ? 1 : 0.94,
        '&:hover': laid.hasKids
          ? { transform: 'translateY(-1px)', opacity: 1 }
          : undefined,
        '&:active': laid.hasKids ? { transform: 'translateY(0)' } : undefined,
        '&:focus-visible': {
          outline: `2px solid ${laid.color}`,
          outlineOffset: 2,
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
        },
      }}
    >
      <ConnectorDot color={laid.color} open={laid.open} hasKids={laid.hasKids} />
      <Box
        sx={{
          width: BRANCH_CARD_W,
          minHeight: ROW_H - 8,
          borderRadius: `${RADIUS_M}px`,
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          ...solidCardFace(style, emphasis),
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1} useFlexGap>
          <Icon sx={{ fontSize: 18, color: laid.color, mt: 0.15, flexShrink: 0 }} aria-hidden />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: laid.color,
                  lineHeight: 1.25,
                  fontSize: '0.8125rem',
                  letterSpacing: 0.2,
                  textTransform: 'uppercase',
                }}
              >
                {laid.node.label}
              </Typography>
              {laid.hasKids ? (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}>
                  {factorLabel}
                </Typography>
              ) : null}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25, lineHeight: 1.3 }}>
              {style.desc}
            </Typography>
            {signal?.type === 'address-differs' ? (
              <Chip
                size="small"
                label="Address differs"
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
                  color: 'warning.dark',
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.warning.main, 0.32),
                }}
              />
            ) : null}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

function SignalCard({ laid, leafMaxW }: { laid: LaidOutNode; leafMaxW: number }) {
  const style = KIND_STYLE[laid.kind];
  const href = laid.node.href ? normalizeHttpUrl(laid.node.href) : null;
  const value = laid.node.value;
  const provenance = leafProvenance(laid.node.leafRole);
  const isCompare =
    laid.node.leafRole === 'declared-address' || laid.node.leafRole === 'ai-footprint';
  const isDistance = laid.node.leafRole === 'address-distance';

  return (
    <Box
      sx={{
        width: leafMaxW,
        borderRadius: `${RADIUS_M}px`,
        px: 1.5,
        py: 1.1,
        bgcolor: 'background.paper',
        border: `1px solid ${alpha(style.color, isCompare ? 0.35 : 0.22)}`,
        boxShadow: isCompare
          ? `inset 3px 0 0 ${alpha('#b45309', 0.5)}, 0 2px 8px ${style.glow}`
          : isDistance
            ? `0 1px 4px ${style.glow}`
            : `0 1px 6px ${style.glow}`,
        ...(isDistance ? { borderStyle: 'dashed' } : null),
        ...(laid.searchHit
          ? { outline: `2px solid ${alpha(laid.color, 0.45)}`, outlineOffset: 1 }
          : null),
      }}
    >
      {provenance ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.7,
              fontSize: '0.65rem',
              color: laid.node.leafRole === 'declared-address' ? 'text.secondary' : laid.color,
            }}
          >
            {provenance.label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
            {provenance.hint}
          </Typography>
        </Stack>
      ) : (
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: laid.color, display: 'block', mb: 0.35, fontSize: '0.7rem' }}
        >
          {laid.node.label}
        </Typography>
      )}
      {value ? (
        href ? (
          <Box
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ ...valueFullSx, color: '#0d6b82', textDecoration: 'underline', fontWeight: 500 }}
          >
            {displayLink(value)}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ ...valueFullSx, color: 'text.primary', fontWeight: isCompare ? 600 : 500 }}>
            {value}
          </Typography>
        )
      ) : null}
      {provenance && laid.node.label !== provenance.label ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.35 }}>
          {laid.node.label}
        </Typography>
      ) : null}
    </Box>
  );
}

function GraphNode({
  laid,
  onToggle,
  leafMaxW,
}: {
  laid: LaidOutNode;
  onToggle: (id: string) => void;
  leafMaxW: number;
}) {
  const isRoot = laid.depth === 0;

  return (
    <Box
      data-osint-anim
      role={laid.isLeaf ? undefined : 'group'}
      aria-label={laid.isLeaf ? undefined : `${laid.node.label} branch`}
      sx={{
        position: 'absolute',
        left: laid.x,
        top: laid.y,
        width: 0,
        height: 0,
        zIndex: isRoot ? 3 : laid.open ? 2 : 1,
        transition: `left ${ANIM_MS}ms ${ANIM_EASE}, top ${ANIM_MS}ms ${ANIM_EASE}`,
        animation: `employerOsintNodeIn ${ANIM_MS}ms ${ANIM_EASE} both`,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          transition: 'none',
        },
      }}
      data-testid={`employer-osint-node-${laid.id}`}
    >
      {laid.isLeaf ? (
        <Box sx={{ position: 'absolute', top: 0, left: CIRCLE_R + 10, transform: 'translateY(-50%)' }}>
          <SignalCard laid={laid} leafMaxW={leafMaxW} />
        </Box>
      ) : isRoot ? (
        <Box sx={{ position: 'absolute', top: 0, right: 0, transform: 'translate(0, -50%)' }}>
          <RootEmployerCard laid={laid} />
        </Box>
      ) : (
        <Box sx={{ position: 'absolute', top: 0, left: 0, transform: 'translateY(-50%)' }}>
          <CategoryCard laid={laid} onToggle={onToggle} />
        </Box>
      )}
    </Box>
  );
}

function MobileIntelligenceTree({
  node,
  query,
  expanded,
  onToggle,
  depth = 0,
  inherited = 'company' as Exclude<FootprintOsintKind, 'leaf'>,
}: {
  node: FootprintOsintTreeNode;
  query: string;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  depth?: number;
  inherited?: Exclude<FootprintOsintKind, 'leaf'>;
}) {
  const kind = resolveKind(node, inherited);
  const style = KIND_STYLE[kind];
  const kids = visibleChildren(node, query);
  const open = expanded.has(node.id) && kids.length > 0;
  const isLeaf = !node.children?.length;

  if (isLeaf) {
    const laid: LaidOutNode = {
      id: node.id,
      node,
      depth,
      x: 0,
      y: 0,
      color: style.color,
      kind,
      hasKids: false,
      open: false,
      isLeaf: true,
      kidCount: 0,
      searchHit: nodeSearchHit(node, query),
    };
    return (
      <Box sx={{ pl: depth * 1.5, py: 0.5 }}>
        <SignalCard laid={laid} leafMaxW={9999} />
      </Box>
    );
  }

  return (
    <Box sx={{ pl: depth > 0 ? 1.5 : 0, py: 0.35 }}>
      {depth === 0 ? (
        <Box
          component={kids.length > 0 ? 'button' : 'div'}
          type={kids.length > 0 ? 'button' : undefined}
          onClick={kids.length > 0 ? () => onToggle(node.id) : undefined}
          aria-expanded={kids.length > 0 ? open : undefined}
          sx={{
            mb: 1.5,
            display: 'block',
            width: '100%',
            m: 0,
            p: 0,
            border: 'none',
            bgcolor: 'transparent',
            textAlign: 'left',
            cursor: kids.length > 0 ? 'pointer' : 'default',
            borderRadius: `${RADIUS_L}px`,
            '&:focus-visible': kids.length > 0 ? { outline: `2px solid ${style.color}`, outlineOffset: 2 } : undefined,
          }}
        >
          <RootEmployerCard
            laid={{
              id: node.id,
              node,
              depth: 0,
              x: 0,
              y: 0,
              color: style.color,
              kind: 'company',
              hasKids: kids.length > 0,
              open,
              isLeaf: false,
              kidCount: kids.length,
              searchHit: false,
            }}
          />
        </Box>
      ) : (
        <CategoryCard
          laid={{
            id: node.id,
            node,
            depth,
            x: 0,
            y: 0,
            color: style.color,
            kind,
            hasKids: kids.length > 0,
            open,
            isLeaf: false,
            kidCount: kids.length,
            searchHit: nodeSearchHit(node, query),
          }}
          onToggle={onToggle}
        />
      )}
      <Collapse in={open} timeout={ANIM_MS}>
        <Stack spacing={0.5} sx={{ mt: 0.5, pl: depth === 0 ? 0 : 1 }}>
          {kids.map((child) => (
            <MobileIntelligenceTree
              key={child.id}
              node={child}
              query={query}
              expanded={expanded}
              onToggle={onToggle}
              depth={depth + 1}
              inherited={kind}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function IntelligenceLegend() {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={1.5}
      sx={{ mt: 1, rowGap: 0.5 }}
      aria-label="Factor group legend"
    >
      {LEGEND_KINDS.map((kind) => {
        const s = KIND_STYLE[kind];
        return (
          <Stack key={kind} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} aria-hidden />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {kind === 'digital' ? 'Web' : kind.charAt(0).toUpperCase() + kind.slice(1)}
            </Typography>
          </Stack>
        );
      })}
      <Typography variant="caption" sx={{ color: 'text.disabled', width: '100%', mt: 0.25 }}>
        Colors indicate factor groups, not pass/fail outcomes.
      </Typography>
    </Stack>
  );
}

export function EmployerFootprintOsintGraph({
  open,
  onClose,
  tree,
  returnFocusRef,
}: {
  open: boolean;
  onClose: () => void;
  tree: FootprintOsintTreeNode;
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const metrics: LayoutMetrics = useMemo(
    () => ({
      originX: CANVAS_PAD_LEFT + ROOT_CARD_W,
      colStep: isTablet ? 280 : COL_STEP,
      leafMaxW: isMobile ? 280 : isTablet ? 300 : 320,
    }),
    [isMobile, isTablet],
  );

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(defaultOsintExpandedIds(tree)));
  const q = query.trim().toLowerCase();

  useEffect(() => {
    setExpanded(new Set(defaultOsintExpandedIds(tree)));
    setQuery('');
  }, [tree]);

  const handleClose = useCallback(() => {
    onClose();
    queueMicrotask(() => returnFocusRef?.current?.focus());
  }, [onClose, returnFocusRef]);

  useEffect(() => {
    if (!q) return;
    setExpanded((prev) => new Set([...prev, ...collectOpenAncestors(tree, q)]));
  }, [q, tree]);

  const visible = useMemo(() => (nodeMatches(tree, q) ? tree : { ...tree, children: [] }), [tree, q]);
  const layout = useMemo(
    () => (isMobile ? { nodes: [], edges: [], width: 0, height: 0 } : layoutTree(visible, q, expanded, metrics)),
    [visible, q, expanded, metrics, isMobile],
  );
  const branchIds = useMemo(() => collectBranchIds(tree), [tree]);
  const allOpen = branchIds.length > 0 && branchIds.every((id) => expanded.has(id));
  const locationSignal = tree.children?.find((c) => c.id === 'location')?.signal;
  const searchHitCount = useMemo(() => (q ? countSearchHits(tree, q) : 0), [tree, q]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const collapseAll = () => setExpanded(new Set(['company']));
  const openAll = () => setExpanded(new Set(branchIds));

  const headerGlassSx = {
    bgcolor: (t: typeof theme) => alpha(t.palette.background.paper, 0.94),
    WebkitBackdropFilter: 'blur(12px) saturate(140%)',
    backdropFilter: 'blur(12px) saturate(140%)',
    '@media (prefers-reduced-transparency: reduce)': {
      bgcolor: 'background.paper',
      WebkitBackdropFilter: 'none',
      backdropFilter: 'none',
    },
    '@supports not ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px)))': {
      bgcolor: 'background.paper',
      WebkitBackdropFilter: 'none',
      backdropFilter: 'none',
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableRestoreFocus={Boolean(returnFocusRef)}
      maxWidth="xl"
      fullWidth
      data-testid="employer-footprint-osint-graph"
      aria-labelledby="employer-osint-graph-title"
      aria-describedby="employer-osint-graph-desc"
      PaperProps={{
        sx: {
          minHeight: 560,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          ...headerGlassSx,
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: 1, color: 'text.secondary', lineHeight: 1.2 }}
            >
              Employer intelligence
            </Typography>
            <Typography id="employer-osint-graph-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}>
              Employer footprint
            </Typography>
            <Typography id="employer-osint-graph-desc" variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
              Advisory OSINT signals — not verified employment
            </Typography>
            <IntelligenceLegend />
            {locationSignal?.type === 'address-differs' ? (
              <Chip
                size="small"
                icon={<PlaceRoundedIcon sx={{ fontSize: '14px !important' }} />}
                label={
                  locationSignal.distanceLabel
                    ? `Location · address differs (${locationSignal.distanceLabel})`
                    : 'Location · address differs'
                }
                sx={{
                  mt: 1,
                  height: 24,
                  fontWeight: 600,
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.warning.main, 0.28),
                }}
              />
            ) : null}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, pt: 0.25 }}>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => (allOpen ? collapseAll() : openAll())}
              data-testid="employer-osint-open-all"
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap', minHeight: 36 }}
            >
              {allOpen ? 'Collapse all' : 'Expand all'}
            </Button>
            <IconButton aria-label="Close employer graph" onClick={handleClose} size="small" edge="end">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <TextField
          size="small"
          fullWidth
          label="Search intelligence"
          placeholder="Search employer intelligence…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mt: 1.5, maxWidth: { xs: '100%', sm: 420 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Search employer intelligence factors' }}
        />
        {q && searchHitCount > 0 ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary', fontWeight: 600 }}>
            {searchHitCount === 1 ? '1 matching signal' : `${searchHitCount} matching signals`}
          </Typography>
        ) : null}
      </Box>

      <DialogContent sx={{ pt: 1.5, pb: 2, px: { xs: 1.5, sm: 2.5 } }}>
        <Box
          sx={{
            position: 'relative',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: `${RADIUS_L}px`,
            overflow: 'auto',
            minHeight: 420,
            maxHeight: '70vh',
            background: (t) =>
              t.palette.mode === 'dark'
                ? `radial-gradient(ellipse 70% 50% at 12% 8%, ${alpha('#3b4d7a', 0.14)}, transparent 55%),
                   radial-gradient(ellipse 60% 45% at 88% 92%, ${alpha('#1a6368', 0.1)}, transparent 50%),
                   ${alpha(t.palette.grey[900], 0.35)}`
                : `radial-gradient(ellipse 70% 50% at 12% 8%, ${alpha('#c9d4ea', 0.45)}, transparent 55%),
                   radial-gradient(ellipse 60% 45% at 88% 92%, ${alpha('#cce8e6', 0.35)}, transparent 50%),
                   linear-gradient(180deg, ${alpha(t.palette.grey[50], 0.95)} 0%, ${alpha(t.palette.grey[100], 0.85)} 100%)`,
          }}
        >
          {q && !nodeMatches(tree, q) && !isMobile ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                No matching signals.
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Try employer, address, website, ownership, or location.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ p: 1.5 }}>
              {!nodeMatches(tree, q) ? (
                <Typography variant="body2" color="text.secondary">
                  No matching signals.
                </Typography>
              ) : (
                <MobileIntelligenceTree node={visible} query={q} expanded={expanded} onToggle={toggle} />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                position: 'relative',
                width: layout.width,
                height: layout.height,
                minWidth: '100%',
                pl: `${CANVAS_PAD_LEFT}px`,
                pt: 1,
                '@keyframes employerOsintNodeIn': {
                  from: { opacity: 0, transform: 'translateX(-6px)' },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
                '@keyframes employerOsintEdgeIn': {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
                '@media (prefers-reduced-motion: reduce)': {
                  '& [data-osint-anim], & path': {
                    animation: 'none !important',
                    transition: 'none !important',
                  },
                },
              }}
            >
              <svg
                width={layout.width}
                height={layout.height}
                style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
                aria-hidden
              >
                {layout.edges.map((edge) => (
                  <path
                    key={`${edge.from.id}-${edge.to.id}`}
                    d={bezier(edge.from, edge.to)}
                    fill="none"
                    stroke={edge.to.color}
                    strokeOpacity={edge.to.open ? 0.42 : 0.28}
                    strokeWidth={edge.to.open ? 1.6 : 1.2}
                    style={{ animation: `employerOsintEdgeIn ${ANIM_MS}ms ${ANIM_EASE} both` }}
                  />
                ))}
              </svg>
              {layout.nodes.map((laid) => (
                <GraphNode key={laid.id} laid={laid} onToggle={toggle} leafMaxW={metrics.leafMaxW} />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
